/* ==========================================================================
   <offer-tiers> — quantity-tier picker for the conversion PDP.

   A theme cannot invent a discount. Each tier only sets the quantity that gets
   submitted; the money actually comes off in checkout via a matching Shopify
   automatic discount. Every figure this script shows was worked out and
   formatted by Liquid — one row per variant, embedded as JSON — so switching
   colour swaps strings rather than recomputing money in the browser. The old
   version rebuilt the number here with a hardcoded decimal point, which quoted
   "€14.68" to a market whose format is "€14,68", and rounded per unit where
   Shopify rounds per line.
   ========================================================================== */
(function () {
  'use strict';

  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));

  const setText = (el, value) => {
    if (el && typeof value === 'string' && value !== '') el.textContent = value;
  };

  /* pdp-details.liquid loads this same file for <detail-list>, so on a product
     page carrying both sections the script executes twice. Re-defining a custom
     element throws, and the throw would abort everything after it — including
     the second define. Guarding is cheaper than splitting the file. */
  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  class OfferTiers extends HTMLElement {
    connectedCallback() {
      this.tiers = $$('[data-tier]', this);
      this.qtyField = $('[name="quantity"]', this);
      this.button = $('[data-atc]', this);
      if (!this.tiers.length) return;

      this.pricing = this.readPricing();

      this.tiers.forEach((tier) => {
        tier.addEventListener('click', () => this.select(tier));
      });

      this.current = this.tiers.find((t) => t.getAttribute('aria-checked') === 'true') || this.tiers[0];
      this.reprice();
      this.select(this.current, true);

      // product.js rewrites [data-atc] on every variant change, which would wipe
      // the tier total off the button. Re-price and re-apply after it has run.
      const picker = $('variant-picker');
      if (picker) {
        picker.addEventListener('click', (event) => {
          if (!event.target.closest('[data-option-value]')) return;
          requestAnimationFrame(() => {
            this.reprice();
            this.select(this.current || this.tiers[0], true);
          });
        });
      }
    }

    readPricing() {
      const node = $('[data-offer-pricing]', this);
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        return null;
      }
    }

    currentVariantId() {
      const field = $('[data-variant-id]');
      return field ? String(field.value) : null;
    }

    /* Repaint every tier for the variant now in the form. Missing data leaves
       the server-rendered markup alone — a stale-but-correct card beats a card
       wiped blank by a variant the JSON does not know about. */
    reprice() {
      if (!this.pricing) return;
      const row = this.pricing[this.currentVariantId()];
      if (!row) return;

      this.tiers.forEach((tier) => {
        const data = row[tier.dataset.tierId];
        if (!data) return;

        tier.dataset.total = data.total;
        setText($('[data-tier-now]', tier), data.unit);
        setText($('[data-tier-title]', tier), data.title);
        setText($('[data-tier-sub]', tier), data.sub);
        setText($('[data-tier-flag]', tier), data.flag);

        const was = $('[data-tier-was]', tier);
        if (was) {
          was.textContent = data.was;
          was.hidden = !data.showWas;
        }

        const save = $('[data-tier-save]', tier);
        if (save) {
          save.textContent = data.save;
          save.hidden = !data.showSave;
        }
      });
    }

    select(tier, silent) {
      if (!tier) return;
      this.current = tier;

      this.tiers.forEach((other) => {
        const on = other === tier;
        other.setAttribute('aria-checked', String(on));
        const perks = $('[data-tier-perks]', other);
        if (perks && perks.dataset.tierPerks === 'selected') perks.hidden = !on;
      });

      const qty = Number(tier.dataset.tier) || 1;
      if (this.qtyField) this.qtyField.value = qty;

      const total = tier.dataset.total;
      if (this.button && total) {
        const label = this.button.dataset.label || 'Add to cart';
        this.button.innerHTML = label + ' &mdash; <span class="num">' + total + '</span>';
      }

      if (!silent) {
        this.dispatchEvent(new CustomEvent('tier:change', { bubbles: true, detail: { tier } }));
      }
    }
  }
  define('offer-tiers', OfferTiers);

  /* --- <detail-list> — the dark spec accordion --------------------------- */
  class DetailList extends HTMLElement {
    connectedCallback() {
      $$('[data-detail]', this).forEach((item) => {
        item.addEventListener('click', () => this.toggle(item));
      });
    }

    toggle(item) {
      const panel = $('[data-detail-panel]', item);
      if (!panel) return;
      const willOpen = panel.hidden;

      $$('[data-detail]', this).forEach((other) => {
        const p = $('[data-detail-panel]', other);
        if (p) p.hidden = true;
        other.setAttribute('aria-expanded', 'false');
        const mark = $('[data-detail-mark]', other);
        if (mark) mark.textContent = '+';
      });

      panel.hidden = !willOpen;
      item.setAttribute('aria-expanded', String(willOpen));
      const mark = $('[data-detail-mark]', item);
      if (mark) mark.textContent = willOpen ? '−' : '+';
    }
  }
  define('detail-list', DetailList);
})();
